import React, { useMemo, useState } from 'react';

// Styles & Assets
import styles from './CreatePreconfiguration.module.css';

// Hooks
import { useTranslation } from 'react-i18next';
import { useCreatePreconfig, useFetchPreconfigData } from '../../../hooks/preconfig-hooks';

// Components
import PreconfigData from '../PreconfigData/PreconfigData';
import PreconfigConstantsTable from '../PreconfigConstantsTable/PreconfigConstantsTable';
import PreconfigPollTable from '../PreconfigPollTable/PreconfigPollTable';
import Modal from '../../UI/Modal/Modal';
import PreconfigErrorDialog from '../PreconfigErrorDialog/PreconfigErrorDialog';

// Interfaces
import { Preconfiguration } from '../../../interfaces/Preconfigurations';
import { Poll, PollFrequency } from '../../../interfaces/Poll';
import { OptionAutocomplete } from '../../../interfaces/Input';

interface Props {
    defaultPreconfig?: Preconfiguration;
}

const CreatePreconfiguration: React.FC<Props> = ({ defaultPreconfig }) => {
    const { t } = useTranslation();

    const [showErrorModal, setShowErrorModal] = useState(false);

    const { sheetParameters } = useFetchPreconfigData();

    const {
        errors,
        name,
        selectedPathologiesIDs,
        selectedProfilesIDs,
        preconfigSheetParameters,
        preconfigInquiries,
        dispatchPreconfig,
        dispatchPreconfigSheetParams,
        dispatchPreconfigInquiries,
        handleSave,
    } = useCreatePreconfig(defaultPreconfig);

    const inquiriesWithData = useMemo(() => {
        return preconfigInquiries
            .map(preconfigInquiry => preconfigInquiry.inquiry)
            .filter((inquiry): inquiry is Poll => Boolean(inquiry));
    }, [preconfigInquiries]);

    const handleSaveClick = () => {
        const saveErrors = handleSave();
        setShowErrorModal(Object.keys(saveErrors).length > 0);
    };

    const handleChangePoll = (
        oldID: number,
        inquiryID: number,
        option: OptionAutocomplete | null,
        poll: Poll | undefined
    ) => {
        if (option === null) {
            dispatchPreconfigInquiries({ type: 'DELETE_INQUIRY', id: oldID });
        } else if (oldID === -1 && poll) {
            dispatchPreconfigInquiries({ type: 'ADD_INQUIRY', poll });
        } else if (oldID !== -1 && poll) {
            dispatchPreconfigInquiries({ type: 'CHANGE_INQUIRY', oldID, poll });
        }
    };

    const handleChangeFreq = (id: number, freq: PollFrequency) => {
        dispatchPreconfigInquiries({ type: 'CHANGE_FREQUENCY', id, freq });
    };

    const handleChangeReminder = (id: number, reminder: string | null) => {
        dispatchPreconfigInquiries({ type: 'CHANGE_REMINDER', id, reminder });
    };

    return (
        <div className={styles.preconfigContainer}>
            <PreconfigData
                newPreconfig={defaultPreconfig === undefined}
                name={name}
                selectedPathologiesIDs={selectedPathologiesIDs}
                selectedPreconfigIDs={selectedProfilesIDs}
                dispatchPreconfig={dispatchPreconfig}
            />

            <div className={styles.tableContainer}>
                <PreconfigConstantsTable
                    sheetParameters={sheetParameters}
                    preconfigSheetParameters={preconfigSheetParameters}
                    dispatchPreconfigSheetParams={dispatchPreconfigSheetParams}
                />

                <PreconfigPollTable
                    preconfigInquiries={preconfigInquiries}
                    handleChangeInquiry={handleChangePoll}
                    handleChangeInquiryFreq={handleChangeFreq}
                    handleChangeInquiryReminder={handleChangeReminder}
                />
            </div>

            <div className={styles.buttonContainer}>
                <button onClick={handleSaveClick} className='button'>
                    {t('save')}
                </button>
            </div>

            <Modal show={showErrorModal} setShowModal={setShowErrorModal}>
                <PreconfigErrorDialog
                    errors={errors}
                    sheetParameters={sheetParameters}
                    inquiries={inquiriesWithData}
                    closeModal={() => setShowErrorModal(false)}
                />
            </Modal>
        </div>
    );
};

export default CreatePreconfiguration;